import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_FUNCTION, EMPTY_LIST } from '../../../../constants/common'
import {
  SUBMENU_PARAM_ACTIVE_VALUE,
  SUBMENU_PARAM_ARCHIVE_VALUE,
  SUBMENU_PARAM_KEY,
} from '../../../../constants/route'
import { useQueryParamSubTab } from '../../../../hooks'
import { getSubSearchParam, isSubArchive } from '../../../../utils/route'
import Button from '../../../Button'
import {
  ENTITY_TAB_CONTRACTS_DEFAULT_QA_NAME,
  ENTITY_TAB_CONTRACTS_SUB_TAB_QA_NAME,
  EntityTabContractTableRow,
  EntityTabSubMenuSubTabs,
  getContractsItemActions,
} from '../../../EntityTab'
import { FilterDivider } from '../../../FilterDivider'
import { PageActions } from '../../../Page'
import { getEmptyStateActions, PageEmptyState } from '../../../PageEmptyState'
import { Pagination } from '../../../Pagination'
import { SelectionToolbar } from '../../../SelectionToolbar'
import { getSubMenuItemNames } from '../../../SubMenu'
import {
  getSelectedSize,
  getTableResetSortingButton,
  Table,
  TableContext,
  TableControlBar,
  TableControlButtons,
  TableMassAction,
} from '../../../Table'
import { TablePagination } from '../../../TablePagination'
import { Container } from '../../Container'

import { ContractsTableSettingsModal } from './ContractsTableSettingsModal'
import {
  CONTRACT_VIEWER_ROLE,
  MOCK_CONTRACTS_SUB_MENU,
} from './ProfileContractsTabInhabitant.constants'
import { useContractsMockTable } from './useContractsMockTable'

import * as styles from './ProfileContractsTabInhabitant.module.css'

import ContractIcon from '../../../../icons/contract.svg'
import PlusIcon from '../../../../icons/plus.svg'

// Library demo for the user (T&L) profile "Contracts" tab, assembled from the
// shared `Table` stack and driven by the local `useContractsMockTable` so it
// renders without a backend or context. The production `EntityTabContracts` uses
// the real `useAsyncTable` stack against the contracts endpoints instead.
function ContractsTabMock({
  qaName = ENTITY_TAB_CONTRACTS_DEFAULT_QA_NAME,
  viewerRole = CONTRACT_VIEWER_ROLE.Staff,
}) {
  const { t } = useTranslation()

  const pageScrollWrapperRef = useRef(null)

  const { activeSubTab, setActiveSubTab } = useQueryParamSubTab({
    allowedValues: getSubMenuItemNames(MOCK_CONTRACTS_SUB_MENU),
    paramKey: SUBMENU_PARAM_KEY,
    readValue: getSubSearchParam,
  })

  const {
    activeCount,
    archiveCount,
    checkIsItemSelected,
    columns,
    exclude,
    handleApply,
    handleConfigOpen,
    handleExclude,
    handleRefresh,
    handleResetOrdering,
    handleSelect,
    handleSelectAll,
    handleSelectAllPages,
    handleSetColumns,
    handleSetOrdering,
    handleSetPagination,
    handleSetSize,
    handleUnselectAllPages,
    isAllSelected,
    isConfigOpen,
    isLoading,
    isRemoveSelection,
    items,
    ordering,
    pagination,
    requiredColumns,
    selectAllPages,
    selected,
    setConfigOpen,
  } = useContractsMockTable({ sub: activeSubTab, viewerRole })

  const isArchiveSubTab = isSubArchive(activeSubTab)

  const hasContracts = items.items.length > 0

  const showEmptyState = !hasContracts && !isLoading

  // Stubbed: the "Add new contract" flow needs the backend (deferred).
  function handleAddContract() {}

  function getRowActions(item) {
    return getContractsItemActions(item, isArchiveSubTab)
  }

  const emptyStateActions = getEmptyStateActions({
    actionName: qaName.addNew,
    isLoading,
    qaName: qaName.addNew,
    shouldShowActions: !isArchiveSubTab,
    text: t('ContractTable.AddNewBtnKey'),
    onClick: handleAddContract,
  })

  return (
    <div className={styles.ContractsTabMock} ref={pageScrollWrapperRef}>
      <EntityTabSubMenuSubTabs
        activeSubTab={activeSubTab}
        counters={{
          [SUBMENU_PARAM_ACTIVE_VALUE]: activeCount,
          [SUBMENU_PARAM_ARCHIVE_VALUE]: archiveCount,
        }}
        qaName={ENTITY_TAB_CONTRACTS_SUB_TAB_QA_NAME}
        subMenu={MOCK_CONTRACTS_SUB_MENU}
        onChange={setActiveSubTab}
      />
      <div className={styles.toolbar}>
        <PageActions className={styles.actions} isLoading={isLoading}>
          {!isArchiveSubTab ? (
            <Button.Text
              beforeIconProps={{ Icon: PlusIcon }}
              isSkeleton={isLoading}
              qaName={qaName.addNew}
              text={t('ContractTable.AddNewBtnKey')}
              onClick={handleAddContract}
            />
          ) : null}
        </PageActions>
      </div>
      {showEmptyState ? (
        <PageEmptyState
          EntityIcon={ContractIcon}
          actions={emptyStateActions}
          text={t('ContractTable.NoDataKey')}
        />
      ) : (
        <>
          <TableControlBar>
            <TableMassAction
              responsive
              actions={EMPTY_LIST}
              allowSelectionWithoutActions={!isArchiveSubTab}
              isAllSelected={isAllSelected}
              isLoading={isLoading}
              isRemoveSelection={isRemoveSelection}
              selectedSize={getSelectedSize({
                exclude,
                pagination,
                selectAllPages,
                selected,
              })}
              setMassAction={EMPTY_FUNCTION}
              onSelectAll={handleSelectAll}
            />
            <PageActions isLoading={isLoading}>
              <TableControlButtons
                ResetSortingButton={getTableResetSortingButton(ordering)}
                configButtonProps={{ onClick: handleConfigOpen }}
                refreshButtonProps={{ onClick: handleRefresh }}
                resetSortingProps={{ onClick: handleResetOrdering }}
              />
              <FilterDivider />
              <Pagination
                responsive
                value={pagination}
                onChange={handleSetPagination}
                onChangeSize={handleSetSize}
              />
            </PageActions>
          </TableControlBar>
          <Table
            TableRow={EntityTabContractTableRow}
            checkIsItemSelected={checkIsItemSelected}
            columns={columns}
            containerRef={pageScrollWrapperRef}
            context={TableContext.User}
            headerRowProps={{
              ordering,
              onApply: handleApply,
              onSetColumns: handleSetColumns,
              onSetOrdering: handleSetOrdering,
            }}
            isLoading={isLoading}
            items={items.items}
            qaName={qaName.table}
            rowProps={{
              getActions: getRowActions,
              hideCheckbox: isArchiveSubTab,
              onCallAction: EMPTY_FUNCTION,
              onClick: EMPTY_FUNCTION,
              onSelect: selectAllPages ? handleExclude : handleSelect,
            }}
          />
        </>
      )}
      {selected.length > 0 || selectAllPages ? (
        <SelectionToolbar
          allSelected={selectAllPages || selected.length === pagination.count}
          count={pagination.count}
          excludeCount={exclude.length}
          selected={selected.length}
          onClose={handleUnselectAllPages}
          onSelectAll={handleSelectAllPages}
        />
      ) : null}
      <TablePagination
        responsive
        value={pagination}
        onChange={handleSetPagination}
      />
      {isConfigOpen ? (
        <ContractsTableSettingsModal
          columns={columns}
          requiredColumns={requiredColumns}
          setColumns={handleSetColumns}
          setConfigOpen={setConfigOpen}
          onApply={handleApply}
        />
      ) : null}
    </div>
  )
}

function ProfileContractsTabInhabitant() {
  return (
    <Container label='Contracts tab'>
      <div style={{ display: 'flex', height: 720 }}>
        <ContractsTabMock />
      </div>
    </Container>
  )
}

export default ProfileContractsTabInhabitant
